import React, { useCallback, useMemo } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import {
	Breadcrumbs,
	Button,
	CollapseSection,
	MainContainer,
	UnderwritingBreadcrumbs,
} from "components";
import { LineDivider } from "components/layout";
import { breadcrumbsReportFromMerchantPart } from "helpers";
import {
	getPotentialsMatches,
	getResultTableData,
	LABELS_GOFAC_RESULT_OWNER,
	LABELS_MATCHES,
} from "mappers";
import {
	useDownloadGofacOwnerDocumentMutation,
	useGofacAuroraOutcomeFieldMutation,
	useGofacOwnerResultQuery,
} from "queries";
import {
	getCurrentUserAccessToken,
	getCurrentUserId,
} from "store/user/user.reducer";
import { UnderwritingTabs, UnderwritingRouteMatchProps } from "types";

import ReportTable from "./reportTable";

import styles from "./reports.module.scss";

const OwnerGofacReport = () => {
	const currentUserId = useSelector(getCurrentUserId);
	const token: string = useSelector(getCurrentUserAccessToken);
	const match: UnderwritingRouteMatchProps = useRouteMatch();

	const isMerchantPage = useMemo(
		() => match?.url?.startsWith("/merchant/"),
		[match?.url],
	);

	const applicationId = useMemo(
		() =>
			isMerchantPage ? match?.params?.applicationId : match?.params?.id,
		[isMerchantPage, match?.params?.applicationId, match?.params?.id],
	);

	const ownerType = match?.params?.ownerType;

	const {
		data: dataOwner,
		isFetching,
		refetch: dataOwnerRefetch,
	} = useGofacOwnerResultQuery({
		id: applicationId,
		ownerType,
	});

	const {
		mutate: downloadGOFACDocument,
		isLoading: isLoadingDownloadDocument,
	} = useDownloadGofacOwnerDocumentMutation();

	const downloadDocument = () =>
		downloadGOFACDocument({
			id: applicationId,
			ownerType,
			token: token,
		});

	const {
		mutate: submitGofacAuroraOutcome,
		isLoading: isLoadingGofacAuroraOutcome,
	} = useGofacAuroraOutcomeFieldMutation();

	const gOFACAuroraOutcomeSubmitHandler = useCallback(
		values => {
			return submitGofacAuroraOutcome({
				id: applicationId,
				values,
				ownerType,
				onSuccessHandler: dataOwnerRefetch,
			});
		},
		[applicationId, dataOwnerRefetch, ownerType, submitGofacAuroraOutcome],
	);

	const ownerResultData = getResultTableData(
		dataOwner?.original,
		dataOwner?.rerun,
		LABELS_GOFAC_RESULT_OWNER,
		"potential_matches",
	);
	const potentialsMatches = getPotentialsMatches(dataOwner, LABELS_MATCHES);
	const isRerun = useMemo(() => !!dataOwner?.rerun, [dataOwner?.rerun]);

	const editableFields = useMemo(() => {
		let fields = {};
		if (
			!isMerchantPage &&
			match?.params?.tab !== UnderwritingTabs.Unassigned.name &&
			match?.params?.tab !== UnderwritingTabs.Historical.name &&
			match?.params?.tab !== UnderwritingTabs.QAUnassigned.name &&
			match?.params?.tab !== UnderwritingTabs.QAComplete.name &&
			dataOwner?.underwriter_id &&
			dataOwner?.underwriter_id === currentUserId
		) {
			fields = {
				...fields,
				outcome: {
					submitHandler: gOFACAuroraOutcomeSubmitHandler,
					isLoading: isLoadingGofacAuroraOutcome,
					reactSelectProps: { menuPlacement: "top" },
				},
			};
		}
		return fields;
	}, [
		currentUserId,
		dataOwner?.underwriter_id,
		gOFACAuroraOutcomeSubmitHandler,
		isLoadingGofacAuroraOutcome,
		isMerchantPage,
		match?.params?.tab,
	]);

	return (
		<MainContainer>
			{isMerchantPage ? (
				<Breadcrumbs
					pages={breadcrumbsReportFromMerchantPart({
						id: match.params.id,
						title: "gOFAC Result - Owner / Signer",
					})}
				/>
			) : (
				<UnderwritingBreadcrumbs match={match} />
			)}
			<p className={clsx(styles.tableTitle, isFetching && "mb-0")}>
				Owner / Signer Result
			</p>
			<ReportTable
				data={ownerResultData}
				isRerun={isRerun}
				isLoading={isFetching}
				editableFields={editableFields}
			/>

			{potentialsMatches?.length !== 0 && !isFetching ? (
				<>
					<p className="text-muted mt-4 mb-3">
						List of Potentials Matches
					</p>
					{potentialsMatches?.map((item, key) => (
						<CollapseSection
							className={styles.collapseWrapper}
							classNameBody={styles.collapseBody}
							header={item?.name}
							key={key}
							children={
								<ReportTable
									data={item?.items}
									isRerun={isRerun}
									disableHeader
									bordered="horizontal"
								/>
							}
						/>
					))}
				</>
			) : null}

			<LineDivider />
			<Button
				type="button"
				value="Download"
				onClick={downloadDocument}
				isLoading={isFetching || isLoadingDownloadDocument}
			/>
		</MainContainer>
	);
};

export default OwnerGofacReport;
